// =============================================================================
// FILE: packages/renderer/src/stores/favorites.store.ts
// PURPOSE: Persists pinned nav shortcuts in localStorage. Each favorite stores
//          the route id, display label, and lucide icon name so it can be
//          rendered in the sidebar without access to the full NAV array.
// =============================================================================

import { useSyncExternalStore } from 'react';

const KEY = 'r2.favorites';

export interface FavoriteItem {
  id:        string;   // unique — typically the Route id
  label:     string;
  iconName:  string;   // lucide icon name as string (fallback: 'Star')
  route:     string;   // Route value to navigate to
  pinnedAt:  number;   // Date.now()
}

function load(): FavoriteItem[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '[]') as FavoriteItem[]; }
  catch { return []; }
}

function save(items: FavoriteItem[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* quota */ }
}

const subs = new Set<() => void>();
let items: FavoriteItem[] = load();
const notify = (): void => { for (const fn of subs) fn(); };

export const favoritesStore = {
  getState: (): FavoriteItem[] => items,

  add(item: Omit<FavoriteItem, 'pinnedAt'>): void {
    if (items.some((f) => f.id === item.id)) return;
    items = [...items, { ...item, pinnedAt: Date.now() }];
    save(items); notify();
  },

  remove(id: string): void {
    items = items.filter((f) => f.id !== id);
    save(items); notify();
  },

  has(id: string): boolean {
    return items.some((f) => f.id === id);
  },

  reorder(from: number, to: number): void {
    const next = [...items];
    const [item] = next.splice(from, 1);
    if (item) next.splice(to, 0, item);
    items = next;
    save(items); notify();
  },

  subscribe: (fn: () => void): (() => void) => {
    subs.add(fn); return () => subs.delete(fn);
  },
};

export function useFavorites(): FavoriteItem[] {
  return useSyncExternalStore(favoritesStore.subscribe, favoritesStore.getState, favoritesStore.getState);
}
